import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { useFonti } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const labelCls = 'text-xs uppercase tracking-wide text-(--color-foreground)';

// Su telefono (iOS/Android) la tastiera mette la maiuscola sulla prima lettera e
// "corregge" il testo: per i campi di ricerca lo disattiviamo, così si parte in
// minuscolo. La ricerca è comunque indifferente a maiuscole/minuscole e accenti.
const searchInputProps = { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } as const;

// Wrapper collassabile: su mobile il campo si richiude (grid-rows 0fr↔1fr); da md sempre aperto.
const collapseCls = (open: boolean) =>
  cn(
    'grid transition-[grid-template-rows] duration-300 ease-out md:grid-rows-[1fr]',
    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
  );

/**
 * Pannello di ricerca della home. Su mobile è visibile solo il campo Testo (+ Trova);
 * Titolo, Quaderni e i filtri avanzati (Dal/Al, Fonte) si rivelano con "Altri filtri",
 * mantenendo l'ordine Titolo → Testo → Quaderni → Dal/Al/Fonte. Form con submit
 * esplicito ("Trova"): lo stato locale è inizializzato dai parametri URL e
 * ri-sincronizzato quando l'URL cambia da fuori (indice, "Torna all'indice", back/forward).
 *
 * Le sezioni collassabili usano un'animazione grid-rows (0fr↔1fr) e si richiudono
 * dopo aver avviato la ricerca. Su desktop (md+) tutto è aperto e il toggle sparisce.
 */
export function SearchPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fonti = useFonti();

  const [titolo, setTitolo] = useState(searchParams.get('titolo') ?? '');
  const [testo, setTesto] = useState(searchParams.get('testo') ?? '');
  const [quaderni, setQuaderni] = useState(searchParams.get('quaderni') ?? '');
  const [dataFrom, setDataFrom] = useState(searchParams.get('dataFrom') ?? '');
  const [dataTo, setDataTo] = useState(searchParams.get('dataTo') ?? '');
  const [fonte, setFonte] = useState(searchParams.get('fonte') ?? '');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setTitolo(searchParams.get('titolo') ?? '');
    setTesto(searchParams.get('testo') ?? '');
    setQuaderni(searchParams.get('quaderni') ?? '');
    setDataFrom(searchParams.get('dataFrom') ?? '');
    setDataTo(searchParams.get('dataTo') ?? '');
    setFonte(searchParams.get('fonte') ?? '');
  }, [searchParams]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next = new URLSearchParams();
    const set = (k: string, v: string) => {
      if (v.trim()) next.set(k, v.trim());
    };
    set('titolo', titolo);
    set('testo', testo);
    set('quaderni', quaderni);
    set('dataFrom', dataFrom);
    set('dataTo', dataTo);
    if (fonte) next.set('fonte', fonte);
    setSearchParams(next);
    setFiltersOpen(false); // su mobile richiude i filtri (animato) dopo la ricerca
  }

  function clearAll() {
    setTitolo('');
    setTesto('');
    setQuaderni('');
    setDataFrom('');
    setDataTo('');
    setFonte('');
    setSearchParams(new URLSearchParams());
  }

  const hasValues = !!(titolo || testo || quaderni || dataFrom || dataTo || fonte);

  return (
    <form onSubmit={onSubmit} aria-label="Ricerca" className="space-y-3">
      {/* Titolo + Testo + Quaderni: su mobile solo Testo; Titolo/Quaderni collassano (ordine invariato) */}
      <div className="flex flex-col md:flex-row md:items-end md:gap-3">
        {/* Titolo: collassa sopra a Testo */}
        <div className={cn(collapseCls(filtersOpen), 'md:flex-1')}>
          <div className="overflow-hidden">
            <div className="pb-3 md:pb-0">
              <Label htmlFor="titolo" className={labelCls}>
                Titolo
              </Label>
              <Input
                id="titolo"
                value={titolo}
                onChange={(e) => setTitolo(e.target.value)}
                placeholder="Cerca nel titolo…"
                className="mt-1.5"
                {...searchInputProps}
              />
            </div>
          </div>
        </div>
        {/* Testo: sempre visibile */}
        <div className="md:flex-1">
          <Label htmlFor="testo" className={labelCls}>
            Testo
          </Label>
          <Input
            id="testo"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder='Cerca nel testo… ("frase esatta", -escluso)'
            className="mt-1.5"
            {...searchInputProps}
          />
        </div>
        {/* Quaderni: collassa sotto a Testo */}
        <div className={cn(collapseCls(filtersOpen), 'md:flex-1')}>
          <div className="overflow-hidden">
            <div className="pt-3 md:pt-0">
              <Label htmlFor="quaderni" className={labelCls}>
                Quaderni
              </Label>
              <Input
                id="quaderni"
                value={quaderni}
                onChange={(e) => setQuaderni(e.target.value)}
                placeholder="Cerca nei titoli dei quaderni…"
                className="mt-1.5"
                {...searchInputProps}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filtri avanzati: richiudibili con animazione su mobile, sempre aperti da md */}
      <div id="filtri-avanzati" className={collapseCls(filtersOpen)}>
        <div className="overflow-hidden">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <div>
              <Label htmlFor="dataFrom" className={labelCls}>
                Dal
              </Label>
              <DatePicker id="dataFrom" className="mt-1.5" placeholder="Data inizio" value={dataFrom} onChange={setDataFrom} />
            </div>
            <div>
              <Label htmlFor="dataTo" className={labelCls}>
                Al
              </Label>
              <DatePicker id="dataTo" className="mt-1.5" placeholder="Data fine" value={dataTo} onChange={setDataTo} />
            </div>
            <div>
              <Label className={labelCls}>Fonte</Label>
              <Select value={fonte || 'all'} onValueChange={(v) => setFonte(v === 'all' ? '' : v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {fonti.data?.fonti.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Toggle filtri avanzati (mobile): in fondo, dopo tutti i filtri */}
      <Button
        type="button"
        variant="outline"
        className="w-full md:hidden"
        onClick={() => setFiltersOpen((v) => !v)}
        aria-expanded={filtersOpen}
        aria-controls="filtri-avanzati"
      >
        <SlidersHorizontal className="h-4 w-4" />
        {filtersOpen ? 'Nascondi' : 'Altri filtri'}
      </Button>

      {/* Azzera filtri a sinistra, Trova (bianco) a destra, stessa riga */}
      <div className="flex items-center gap-2">
        {hasValues && (
          <Button type="button" variant="ghost" size="sm" onClick={clearAll} className="text-(--color-foreground)/70 hover:bg-black/5">
            <X className="h-4 w-4" /> Azzera filtri
          </Button>
        )}
        <Button type="submit" variant="outline" className="ml-auto bg-white">
          Trova
        </Button>
      </div>
    </form>
  );
}
